import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { LogIn, Loader2, ShieldCheck } from 'lucide-react';

const AdminLogin = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading, processSessionId } = useAuth();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [initialCheckDone, setInitialCheckDone] = useState(false);

  useEffect(() => {
    // Wait for initial auth check to complete
    if (!loading && !initialCheckDone) {
      setInitialCheckDone(true);
      
      // Check if already authenticated
      if (isAuthenticated) {
        navigate('/admin/dashboard');
        return;
      }

      // Check for session_id in URL fragment
      const hash = window.location.hash;
      if (hash && hash.includes('session_id=')) {
        const sessionId = hash.split('session_id=')[1].split('&')[0];
        handleSessionId(sessionId);
      }
    }
  }, [isAuthenticated, loading, initialCheckDone, navigate]);

  const handleSessionId = async (sessionId) => {
    setProcessing(true);
    setError('');

    try {
      await processSessionId(sessionId);
      // Clear URL fragment
      window.history.replaceState(null, '', window.location.pathname);
      // Navigate to dashboard
      navigate('/admin/dashboard');
    } catch (err) {
      setError(err.message || 'Authentication failed. Please try again.');
      setProcessing(false);
      // Clear URL fragment
      window.history.replaceState(null, '', window.location.pathname);
    }
  };

  const handleLogin = () => {
    const redirectUrl = `${window.location.origin}/admin`;
    const authUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    window.location.href = authUrl;
  };

  if (loading || processing) {
    return (
      <div className="min-h-screen bg-[#0f1419] flex items-center justify-center px-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-emerald-400 animate-spin mx-auto mb-4" />
          <p className="text-gray-400">{processing ? 'Authenticating...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f1419] flex items-center justify-center px-4">
      <Card className="bg-[#1a1d29] border-gray-800 p-8 max-w-md w-full">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShieldCheck className="w-8 h-8 text-emerald-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Admin Portal</h1>
          <p className="text-gray-400 text-sm">
            Sign in with your Google account to access the admin dashboard
          </p>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 mb-6">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        <Button
          onClick={handleLogin}
          className="w-full bg-emerald-500 hover:bg-emerald-600 text-white transition-all duration-300"
        >
          <LogIn className="mr-2" size={18} />
          Sign in with Google
        </Button>

        <p className="text-gray-500 text-xs text-center mt-6">
          Only authorized administrators can access this portal
        </p>
      </Card>
    </div>
  );
};

export default AdminLogin;
